/*
 * Copyright (c) 2024 Bluemate B.V.
 */
import './style/platformUsage.scss';

import React, {ReactNode, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    Checkbox,
    CloseButton,
    ElementPosition,
    Loader,
    NumberString,
    WithBackground
} from '@dvrd/dvr-controls';

import Chart from 'react-apexcharts';

import {ApexOptions} from 'apexcharts';

import IDate from '@dvrd/idate';

import {BluemateButton, BluemateInput, BluemateSelect, BluemateNumberInput, BluemateTooltip} from '../../controls';

import BluemateSwitch from '../../controls/switch/bluemateSwitch';

import InfoIcon from '../../icon/infoIcon';

import {useBreakpoint} from '../../../util/useBreakpoint';

import {
    AnalyticsSettings,
    BucketOption,
    BreakdownOption,
    EditableSettingsState,
    EventNameOption,
    FilterState,
    MetricOption,
    PlatformUsageData,
    PushDeliveryPeriodByPlatform,
    PushDeliveryPeriodStats,
    PushDeliveryStats,
    TopTableItem
} from './platformUsageController';

interface Props {
    loading: boolean;
    data: PlatformUsageData | null;
    filters: FilterState;
    isDayBucketAllowed: boolean;
    onUpdateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
    onDrillDown: (featureKey: string) => void;
    onClearDrillDown: () => void;
    onResetFilters: () => void;
    onRefresh: () => void;
    onRunAggregation: () => void;
    cooldownRemainingSeconds: number;
    analyticsSettings: AnalyticsSettings | null;
    editableSettings: EditableSettingsState | null;
    settingsLoading: boolean;
    settingsSaving: boolean;
    settingsHasChanges: boolean;
    clearDataModalActive: boolean;
    clearDataConfirmed: boolean;
    clearDataLoading: boolean;
    onUpdateEditableSetting: <K extends keyof EditableSettingsState>(key: K, value: EditableSettingsState[K]) => void;
    onSaveSettings: () => void;
    onOpenClearDataModal: () => void;
    onCloseClearDataModal: () => void;
    onSetClearDataConfirmed: (confirmed: boolean) => void;
    onClearAllData: () => void;
}

// Chart helper functions
interface YAxisConfig {
    min: number;
    max: number;
    tickAmount: number;
}

function buildYAxisConfig(values: number[], options?: {padding?: number; startFromZero?: boolean}): YAxisConfig {
    const {padding = 0.1, startFromZero = true} = options ?? {};
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values, 1);
    const yAxisMin = startFromZero ? 0 : Math.max(0, Math.floor(minValue * (1 - padding)));
    const yAxisMax = Math.ceil(maxValue * (1 + padding));
    const range = yAxisMax - yAxisMin;
    const tickAmount = Math.min(Math.max(range, 2), 10);
    return {min: yAxisMin, max: yAxisMax, tickAmount};
}

// Overrides are merged one level deep so a call site that customizes e.g.
// `legend` keeps the compact defaults it does not mention.
function mergeChartOptions(base: ApexOptions, overrides?: Partial<ApexOptions>): ApexOptions {
    if (!overrides) return base;
    const merged: Record<string, unknown> = {...(base as Record<string, unknown>)};
    for (const [key, value] of Object.entries(overrides as Record<string, unknown>)) {
        const current = merged[key];
        const mergeable = (candidate: unknown) =>
            !!candidate && typeof candidate === 'object' && !Array.isArray(candidate);
        merged[key] = mergeable(current) && mergeable(value) ? {...(current as object), ...(value as object)} : value;
    }
    return merged as ApexOptions;
}

function buildBaseChartOptions(
    categories: string[],
    yAxisConfig: YAxisConfig,
    title: string,
    chartType: 'line' | 'area' = 'line',
    overrides?: Partial<ApexOptions>,
    compact: boolean = false
): ApexOptions {
    const base: ApexOptions = {
        chart: {
            type: chartType,
            toolbar: {show: false},
            zoom: {enabled: false}
        },
        xaxis: {
            categories,
            labels: {rotate: -45, rotateAlways: categories.length > 10}
        },
        yaxis: {
            min: yAxisConfig.min,
            max: yAxisConfig.max,
            tickAmount: yAxisConfig.tickAmount,
            reversed: false,
            labels: {
                formatter: (val: number) => Math.round(val).toString()
            }
        },
        stroke: {curve: 'smooth', width: 2},
        markers: {
            size: categories.length <= 3 ? 6 : 4,
            strokeWidth: 2,
            hover: {size: 8}
        },
        dataLabels: {enabled: false},
        title: {
            text: title,
            align: 'left',
            style: {fontSize: '14px', fontWeight: 600}
        }
    };
    if (!compact) return {...base, ...overrides};

    // Phone-sized canvas: fewer ticks, smaller type, smaller markers. Apex draws
    // to SVG with inline styles, so none of this is reachable from a media query.
    // Period labels drop their year ("2026-07-15" → "07-15", "2026-W28" → "W28")
    // so they stay readable instead of being ellipsized to the year.
    const shortPeriod = (value: string) => (/^\d{4}-/.test(value) ? value.slice(5) : value);
    const compactBase: ApexOptions = {
        ...base,
        chart: {...base.chart, parentHeightOffset: 0},
        xaxis: {
            categories,
            tickAmount: Math.min(categories.length, 5),
            labels: {
                rotate: -45,
                rotateAlways: categories.length > 4,
                hideOverlappingLabels: true,
                formatter: (value: string) => shortPeriod(String(value ?? '')),
                style: {fontSize: '10px'}
            }
        },
        yaxis: {
            min: yAxisConfig.min,
            max: yAxisConfig.max,
            tickAmount: Math.min(yAxisConfig.tickAmount, 5),
            reversed: false,
            labels: {
                formatter: (val: number) => Math.round(val).toString(),
                style: {fontSize: '10px'}
            }
        },
        markers: {size: categories.length <= 3 ? 5 : 3, strokeWidth: 2, hover: {size: 6}},
        legend: {fontSize: '11px', itemMargin: {horizontal: 5, vertical: 2}},
        title: {text: title, align: 'left', style: {fontSize: '12px', fontWeight: 600}}
    };
    return mergeChartOptions(compactBase, overrides);
}

export default function PlatformUsage(props: Props) {
    const {t} = useTranslation();
    const BUCKET_OPTIONS: {value: BucketOption; label: string}[] = [
        {value: 'auto', label: t('admin.platformUsage.bucket.auto', 'Auto')},
        {value: 'day', label: t('admin.platformUsage.bucket.day', 'Dag')},
        {value: 'week', label: t('admin.platformUsage.bucket.week', 'Week')},
        {value: 'month', label: t('admin.platformUsage.bucket.month', 'Maand')}
    ];
    const METRIC_OPTIONS: {value: MetricOption; label: string}[] = [
        {value: 'unique_users', label: t('admin.platformUsage.metric.uniqueUsers', 'Unieke gebruikers')},
        {value: 'total_events', label: t('admin.platformUsage.metric.totalEvents', 'Totaal events')}
    ];
    const EVENT_OPTIONS: {value: EventNameOption; label: string}[] = [
        {value: 'page_view', label: t('admin.platformUsage.event.pageView', 'Page views')},
        {value: 'action_success', label: t('admin.platformUsage.event.actionSuccess', 'Acties (succes)')},
        {value: 'action_failed', label: t('admin.platformUsage.event.actionFailed', 'Acties (gefaald)')}
    ];
    const BREAKDOWN_OPTIONS: {value: BreakdownOption; label: string}[] = [
        {value: 'feature_group', label: t('admin.platformUsage.breakdown.featureGroup', 'Feature groep')},
        {value: 'feature_key', label: t('admin.platformUsage.breakdown.featureKey', 'Feature')}
    ];
    const TOP_N_OPTIONS: {value: number; label: string}[] = [
        {value: 5, label: '5'},
        {value: 10, label: '10'},
        {value: 0, label: t('admin.platformUsage.topN.all', 'Alles')}
    ];
    const TOP_TABLE_METRICS: {key: string; label: string; help: string; value: (item: TopTableItem) => string}[] = [
        {
            key: 'views_total',
            label: t('admin.platformUsage.tbl.viewsTotal', 'Views (totaal)'),
            help: 'Het totale aantal paginaweergaven voor deze feature in de geselecteerde periode.',
            value: item => String(item.views_total)
        },
        {
            key: 'views_unique',
            label: t('admin.platformUsage.tbl.viewsUnique', 'Views (uniek)'),
            help: 'Het aantal unieke gebruikers dat deze pagina heeft bekeken.',
            value: item => String(item.views_unique)
        },
        {
            key: 'actions_total',
            label: t('admin.platformUsage.tbl.actionsTotal', 'Acties (totaal)'),
            help: 'Het totale aantal uitgevoerde acties (bijv. opslaan, exporteren) voor deze feature.',
            value: item => String(item.actions_total)
        },
        {
            key: 'actions_unique',
            label: t('admin.platformUsage.tbl.actionsUnique', 'Acties (uniek)'),
            help: 'Het aantal unieke gebruikers dat een actie heeft uitgevoerd.',
            value: item => String(item.actions_unique)
        },
        {
            key: 'conversion_pct',
            label: t('admin.platformUsage.tbl.conversion', 'Conversie %'),
            help: 'Het percentage van paginaweergaven dat resulteerde in een actie. Formule: (Acties totaal / Views totaal) × 100.',
            value: item => `${item.conversion_pct}%`
        }
    ];
    const {isDesktop, isMobile} = useBreakpoint();
    const compactLayout = !isDesktop;
    const chartHeight = isMobile ? 240 : 300;
    const {
        loading,
        data,
        filters,
        isDayBucketAllowed,
        onUpdateFilter,
        onDrillDown,
        onClearDrillDown,
        onResetFilters,
        onRefresh,
        onRunAggregation,
        cooldownRemainingSeconds,
        analyticsSettings,
        editableSettings,
        settingsLoading,
        settingsSaving,
        settingsHasChanges,
        clearDataModalActive,
        clearDataConfirmed,
        clearDataLoading,
        onUpdateEditableSetting,
        onSaveSettings,
        onOpenClearDataModal,
        onCloseClearDataModal,
        onSetClearDataConfirmed,
        onClearAllData
    } = props;

    // Today's date for max date restriction (cannot select future dates)
    const today = useMemo(() => new IDate().format('YYYY-MM-DD'), []);

    // Has any data in the system at all (independent of filters)
    const hasAnyData = data?.has_any_data ?? false;
    // Has results for the current filter settings
    const hasFilteredResults = data?.timeseries_global && data.timeseries_global.length > 0;
    // Check if date range is valid (fromDate <= toDate)
    const isDateRangeValid = filters.fromDate <= filters.toDate;

    function onClickBack() {
        history.back();
    }

    function renderStatRow(label: string, value: number) {
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label' title={label}>
                    {label}
                </span>
                <span className='stat-value'>{value}</span>
            </div>
        );
    }

    function renderStatRowWithActive(label: string, valueAll: number, valueActive: number) {
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label' title={label}>
                    {label}
                </span>
                <span className='stat-value'>{valueAll}</span>
                <span className='stat-value active'>{valueActive}</span>
            </div>
        );
    }

    function renderEmailStats() {
        if (!data) return null;
        const {email_stats} = data;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>{t('admin.platformUsage.emailSettingsTitle', 'E-mail instellingen')}</h3>
                    {renderHelp(
                        <>
                            <strong>{t('admin.platformUsage.emailSettingsTitle', 'E-mail instellingen')}</strong>
                            <p>
                                {t(
                                    'admin.platformUsage.emailTooltipIntro',
                                    'Overzicht van e-mailinstellingen per account.'
                                )}
                            </p>
                            <ul>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.emailTooltipAutoLabel', 'Automatisch mailen aan/uit')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.emailTooltipAutoDesc',
                                        'Of automatische e-mailverzending is ingeschakeld (minstens één actieve e-mail flow).'
                                    )}
                                </li>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.emailTooltipTrackingLabel', 'No-reply tracking')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.emailTooltipTrackingDesc',
                                        'Of no-reply tracking (met leesbevestigingen) is ingeschakeld.'
                                    )}
                                </li>
                                <li>
                                    <strong>{t('admin.platformUsage.invoiceOnly', 'Alleen factuur e-mail')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.emailTooltipInvoiceDesc',
                                        'Accounts die alleen factuur e-mails versturen.'
                                    )}
                                </li>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.invoiceReview', 'Factuur + review verzoek')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.emailTooltipReviewDesc',
                                        'Accounts die zowel factuur als review e-mails versturen.'
                                    )}
                                </li>
                            </ul>
                            <p className='column-explanation'>
                                <strong>{t('admin.platformUsage.allAccounts', 'Alle accounts')}:</strong>{' '}
                                {t('admin.platformUsage.columnAllDesc', 'Alle accounts.')}
                                <br />
                                <strong>{t('admin.platformUsage.active', 'Betalende eigenaren')}:</strong>{' '}
                                {t(
                                    'admin.platformUsage.columnActiveDesc',
                                    'Unieke eigenaren met abonnementsstatus active, past_due of unpaid, net als Actief bij Gebruikers; exclusief het demo-account. Zonder eigen winkel telt een gebruiker bij uit. Een eigenaar telt eenmaal per categorie als minstens één eigen winkel voldoet. Uit betekent: in geen enkele eigen winkel ingeschakeld. Factuurcategorieën worden per winkel bepaald; een eigenaar kan in beide categorieën voorkomen.'
                                )}
                            </p>
                        </>
                    )}
                </div>
                <div className='stats-table with-active'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>{t('admin.platformUsage.allAccounts', 'Alle accounts')}</span>
                        <span className='stat-value active'>
                            {t('admin.platformUsage.active', 'Betalende eigenaren')}
                        </span>
                    </div>
                    {renderStatRowWithActive(
                        t('admin.platformUsage.autoMailOn', 'Automatisch mailen aan'),
                        email_stats.auto_mail_enabled,
                        email_stats.auto_mail_enabled_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.autoMailOff', 'Automatisch mailen uit'),
                        email_stats.auto_mail_disabled,
                        email_stats.auto_mail_disabled_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.noReplyOn', 'No-reply tracking aan'),
                        email_stats.email_tracking_enabled,
                        email_stats.email_tracking_enabled_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.noReplyOff', 'No-reply tracking uit'),
                        email_stats.email_tracking_disabled,
                        email_stats.email_tracking_disabled_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.invoiceOnly', 'Alleen factuur e-mail'),
                        email_stats.invoice_only,
                        email_stats.invoice_only_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.invoiceReview', 'Factuur + review verzoek'),
                        email_stats.invoice_and_review,
                        email_stats.invoice_and_review_active
                    )}
                </div>
            </div>
        );
    }

    function renderNotificationStats() {
        if (!data) return null;
        const {by_type, by_type_active, module_paid, module_trial, module_total} = data.notification_stats;
        return (
            <>
                <div className='stats-section'>
                    <div className='section-header'>
                        <h3>{t('admin.platformUsage.notificationsByType', 'Notificaties (per type)')}</h3>
                        {renderHelp(
                            <>
                                <strong>
                                    {t('admin.platformUsage.notificationsByType', 'Notificaties (per type)')}
                                </strong>
                                <p>
                                    {t(
                                        'admin.platformUsage.notifTypeTooltipIntro',
                                        'Aantal accounts met actieve notificaties per type.'
                                    )}
                                </p>
                                <ul>
                                    <li>
                                        <strong>Reviews:</strong>{' '}
                                        {t('admin.platformUsage.notifTypeReviews', 'Notificaties over nieuwe reviews.')}
                                    </li>
                                    <li>
                                        <strong>Buy Box:</strong>{' '}
                                        {t(
                                            'admin.platformUsage.notifTypeBuyBox',
                                            'Notificaties over Buy Box wijzigingen.'
                                        )}
                                    </li>
                                    <li>
                                        <strong>Stock:</strong>{' '}
                                        {t(
                                            'admin.platformUsage.notifTypeStock',
                                            'Notificaties over voorraadwijzigingen.'
                                        )}
                                    </li>
                                    <li>
                                        <strong>{t('admin.platformUsage.orderFree', 'Order (gratis)')}:</strong>{' '}
                                        {t('admin.platformUsage.notifTypeOrder', 'Gratis ordernotificaties.')}
                                    </li>
                                </ul>
                                <p className='column-explanation'>
                                    <strong>{t('admin.platformUsage.allAccounts', 'Alle accounts')}:</strong>{' '}
                                    {t('admin.platformUsage.columnAllDesc', 'Alle accounts.')}
                                    <br />
                                    <strong>{t('admin.platformUsage.active', 'Betalende eigenaren')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.columnActiveDesc',
                                        'Unieke eigenaren met abonnementsstatus active, past_due of unpaid, net als Actief bij Gebruikers; exclusief het demo-account. Zonder eigen winkel telt een gebruiker bij uit. Een eigenaar telt eenmaal per categorie als minstens één eigen winkel voldoet. Uit betekent: in geen enkele eigen winkel ingeschakeld. Factuurcategorieën worden per winkel bepaald; een eigenaar kan in beide categorieën voorkomen.'
                                    )}
                                </p>
                            </>
                        )}
                    </div>
                    <div className='stats-table with-active'>
                        <div className='stat-row header'>
                            <span className='stat-label'></span>
                            <span className='stat-value'>{t('admin.platformUsage.allAccounts', 'Alle accounts')}</span>
                            <span className='stat-value active'>
                                {t('admin.platformUsage.active', 'Betalende eigenaren')}
                            </span>
                        </div>
                        {renderStatRowWithActive('Reviews', by_type.REVIEWS, by_type_active.REVIEWS)}
                        {renderStatRowWithActive('Buy Box', by_type.BUY_BOX, by_type_active.BUY_BOX)}
                        {renderStatRowWithActive('Stock', by_type.STOCK, by_type_active.STOCK)}
                        {renderStatRowWithActive(
                            t('admin.platformUsage.orderFree', 'Order (gratis)'),
                            by_type.ORDER,
                            by_type_active.ORDER
                        )}
                    </div>
                </div>
                <div className='stats-section'>
                    <div className='section-header'>
                        <h3>{t('admin.platformUsage.notificationsModule', 'Notificaties module (abonnement)')}</h3>
                        {renderHelp(
                            <>
                                <strong>
                                    {t('admin.platformUsage.notificationsModule', 'Notificaties module (abonnement)')}
                                </strong>
                                <p>
                                    {t(
                                        'admin.platformUsage.notifModuleTooltipIntro',
                                        'Status van de betaalde notificatiemodule per abonnement.'
                                    )}
                                </p>
                                <ul>
                                    <li>
                                        <strong>
                                            {t('admin.platformUsage.afterTrialPaying', 'Na trial (betalend)')}:
                                        </strong>{' '}
                                        {t(
                                            'admin.platformUsage.notifModuleAfterTrialDesc',
                                            'Abonnementen die betalen voor notificaties (na trial periode).'
                                        )}
                                    </li>
                                    <li>
                                        <strong>{t('admin.platformUsage.inTrial', 'In trial')}:</strong>{' '}
                                        {t(
                                            'admin.platformUsage.notifModuleInTrialDesc',
                                            'Abonnementen die de notificatiemodule in trial hebben.'
                                        )}
                                    </li>
                                    <li>
                                        <strong>{t('admin.platformUsage.totalActive', 'Totaal actief')}:</strong>{' '}
                                        {t(
                                            'admin.platformUsage.notifModuleTotalDesc',
                                            'Totaal aantal actieve notificatiemodules (betalend + trial + gratis, exclusief geannuleerd).'
                                        )}
                                    </li>
                                </ul>
                            </>
                        )}
                    </div>
                    <div className='stats-table'>
                        {renderStatRow(t('admin.platformUsage.afterTrialPaying', 'Na trial (betalend)'), module_paid)}
                        {renderStatRow(t('admin.platformUsage.inTrial', 'In trial'), module_trial)}
                        {renderStatRow(t('admin.platformUsage.totalActive', 'Totaal actief'), module_total)}
                    </div>
                </div>
            </>
        );
    }

    function renderAdditionalUsersStats() {
        if (!data) return null;
        const {additional_users_stats} = data;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>{t('admin.platformUsage.additionalUsers', 'Extra gebruikers')}</h3>
                    {renderHelp(
                        <>
                            <strong>{t('admin.platformUsage.additionalUsers', 'Extra gebruikers')}</strong>
                            <p>
                                {t(
                                    'admin.platformUsage.additionalTooltipIntro',
                                    'Extra gebruikers bij alle accounts en bij betalende eigenaren.'
                                )}
                            </p>
                            <ul>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.accountsAdditional', 'Met extra gebruikers')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.additionalTooltipAccountsDesc',
                                        'Links: accounts met meer dan één gebruiker. Rechts: unieke betalende eigenaren met minstens één extra gebruiker in hun eigen winkels.'
                                    )}
                                </li>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.totalAdditional', 'Totaal extra gebruikers')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.additionalTooltipTotalDesc',
                                        'Links: som van extra gebruikers per account. Rechts: unieke extra personen in winkels van betalende eigenaren, exclusief eigenaren in die winkel. Dezelfde persoon telt eenmaal, ook bij meerdere winkels. Dit aantal kan hoger zijn dan het aantal betalende eigenaren.'
                                    )}
                                </li>
                            </ul>
                            <p className='column-explanation'>
                                <strong>{t('admin.platformUsage.allAccounts', 'Alle accounts')}:</strong>{' '}
                                {t('admin.platformUsage.columnAllDesc', 'Alle accounts.')}
                                <br />
                                <strong>{t('admin.platformUsage.active', 'Betalende eigenaren')}:</strong>{' '}
                                {t(
                                    'admin.platformUsage.columnActiveDesc',
                                    'Unieke eigenaren met abonnementsstatus active, past_due of unpaid, net als Actief bij Gebruikers; exclusief het demo-account. Zonder eigen winkel telt een gebruiker bij uit. Een eigenaar telt eenmaal per categorie als minstens één eigen winkel voldoet. Uit betekent: in geen enkele eigen winkel ingeschakeld. Factuurcategorieën worden per winkel bepaald; een eigenaar kan in beide categorieën voorkomen.'
                                )}
                            </p>
                        </>
                    )}
                </div>
                <div className='stats-table with-active'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>{t('admin.platformUsage.allAccounts', 'Alle accounts')}</span>
                        <span className='stat-value active'>
                            {t('admin.platformUsage.active', 'Betalende eigenaren')}
                        </span>
                    </div>
                    {renderStatRowWithActive(
                        t('admin.platformUsage.accountsAdditional', 'Met extra gebruikers'),
                        additional_users_stats.accounts_with_additional_users,
                        additional_users_stats.accounts_with_additional_users_active
                    )}
                    {renderStatRowWithActive(
                        t('admin.platformUsage.totalAdditional', 'Totaal extra gebruikers'),
                        additional_users_stats.total_additional_users,
                        additional_users_stats.total_additional_users_active
                    )}
                </div>
            </div>
        );
    }

    function formatDeliveryRate(stats: PushDeliveryPeriodStats | null): string {
        if (!stats || stats.sent === 0 || stats.delivery_rate === null) return '—';
        return `${stats.delivery_rate}% (${stats.confirmed}/${stats.sent})`;
    }

    function deliveryRateColor(stats: PushDeliveryPeriodStats | null): string | undefined {
        if (!stats || stats.sent === 0 || stats.delivery_rate === null) return undefined;
        if (stats.delivery_rate >= 90) return '#28a745';
        if (stats.delivery_rate >= 70) return '#e6a700';
        return '#dc3545';
    }

    function renderDeliveryRateRow(label: string, period: PushDeliveryPeriodByPlatform | null) {
        const platforms = ['total', 'android', 'ios', 'other'] as const;
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label'>{label}</span>
                {platforms.map(p => {
                    const stats = period?.[p] ?? null;
                    const color = deliveryRateColor(stats);
                    return (
                        <span
                            key={p}
                            className={`stat-value${p !== 'total' ? ' platform' : ''}`}
                            style={color ? {color} : undefined}
                        >
                            {formatDeliveryRate(stats)}
                        </span>
                    );
                })}
            </div>
        );
    }

    function renderDeliveryCountRow(
        label: string,
        period: PushDeliveryPeriodByPlatform | null,
        field: keyof PushDeliveryPeriodStats
    ) {
        const platforms = ['total', 'android', 'ios', 'other'] as const;
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label'>{label}</span>
                {platforms.map(p => (
                    <span key={p} className={`stat-value${p !== 'total' ? ' platform' : ''}`}>
                        {period?.[p]?.[field] ?? 0}
                    </span>
                ))}
            </div>
        );
    }

    function renderPlatformCountRow(
        label: string,
        byPlatform: PushDeliveryStats['subscriptions_by_platform'],
        field: 'active' | 'inactive'
    ) {
        const platforms = ['total', 'android', 'ios', 'other'] as const;
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label'>{label}</span>
                {platforms.map(p => (
                    <span key={p} className={`stat-value${p !== 'total' ? ' platform' : ''}`}>
                        {byPlatform[p][field]}
                    </span>
                ))}
            </div>
        );
    }

    function renderPwaInstallStats() {
        if (!data) return null;
        const {push_delivery_stats} = data;
        const bp = push_delivery_stats.subscriptions_by_platform;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>{t('admin.platformUsage.pwaInstallTitle', 'Push-apparaten')}</h3>
                    {renderHelp(
                        <>
                            <strong>{t('admin.platformUsage.pwaInstallTitle', 'Push-apparaten')}</strong>
                            <p>
                                {t(
                                    'admin.platformUsage.pwaInstallInfo',
                                    'Overzicht van unieke push-apparaten per platform (dubbele abonnementen van hetzelfde apparaat tellen als één; alleen recent geziene abonnementen tellen als actief). Dit geeft een indicatie van het PWA-gebruik, aangezien push-abonnementen een geïnstalleerde app vereisen op mobiele apparaten.'
                                )}
                            </p>
                            <ul>
                                <li>
                                    <strong>{t('admin.platformUsage.pushActiveDevices', 'Actieve apparaten')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.pushActiveDevicesInfo',
                                        'Aantal unieke apparaten met een actief web push abonnement dat recent is gezien (binnen de ingestelde bewaarperiode). Dubbele abonnementen van hetzelfde apparaat tellen als één.'
                                    )}
                                </li>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.pushInactiveDevices', 'Inactieve apparaten')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.pushInactiveDevicesInfo',
                                        'Apparaten zonder recent actief abonnement: gedeactiveerd door een mislukte bezorging (410/404), niet gezien binnen de ingestelde bewaarperiode, of vervangen door een nieuwere registratie van hetzelfde apparaat.'
                                    )}
                                </li>
                            </ul>
                        </>
                    )}
                </div>
                <div className='stats-table with-platforms'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>{t('admin.platformUsage.total', 'Total')}</span>
                        <span className='stat-value platform'>Android</span>
                        <span className='stat-value platform'>iOS</span>
                        <span className='stat-value platform'>{t('admin.platformUsage.other', 'Other')}</span>
                    </div>
                    {renderPlatformCountRow(
                        t('admin.platformUsage.pushActiveDevices', 'Actieve apparaten'),
                        bp,
                        'active'
                    )}
                    {renderPlatformCountRow(
                        t('admin.platformUsage.pushInactiveDevices', 'Inactieve apparaten'),
                        bp,
                        'inactive'
                    )}
                </div>
            </div>
        );
    }

    function renderPushDeliveryStats() {
        if (!data) return null;
        const {push_delivery_stats} = data;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>{t('admin.platformUsage.pushDeliveryTitle', 'Web Push Bezorging')}</h3>
                    {renderHelp(
                        <>
                            <strong>{t('admin.platformUsage.pushDeliveryTooltipTitle', 'Web Push Bezorging')}</strong>
                            <p>
                                {t(
                                    'admin.platformUsage.pushDeliveryInfo1',
                                    'Overzicht van web push bezorgstatistieken.'
                                )}
                            </p>
                            <ul>
                                <li>
                                    <strong>
                                        {t('admin.platformUsage.pushDeliveryRate', 'Bevestigingspercentage')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.pushDeliveryRateInfo',
                                        'Percentage van verzonden pushes dat door de service worker als ontvangen is bevestigd. Naast het percentage staat het aantal (bevestigd/verzonden).'
                                    )}
                                </li>
                            </ul>
                            <p>
                                {t(
                                    'admin.platformUsage.pushDeliveryNote',
                                    'De werkelijke bezorging ligt hoger dan het bevestigingspercentage: bevestigingen vereisen een actieve sessie (cookie) en dubbele bevestigingen van dezelfde push tellen niet mee.'
                                )}
                            </p>
                            <p>
                                {t(
                                    'admin.platformUsage.pushDeliveryOfflineNote',
                                    'Offline apparaten: wanneer een apparaat offline is, wordt het pushbericht tot 24 uur bewaard door de push-service. De bevestiging wordt pas geteld wanneer het apparaat weer online komt. Hierdoor kan het percentage bij "afgelopen uur" tijdelijk lager lijken. De periodes "vandaag" en "deze week" geven een nauwkeuriger beeld doordat ze een breder tijdvenster beslaan.'
                                )}
                            </p>
                        </>
                    )}
                </div>
                <div className='stats-table with-platforms'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>{t('admin.platformUsage.total', 'Total')}</span>
                        <span className='stat-value platform'>Android</span>
                        <span className='stat-value platform'>iOS</span>
                        <span className='stat-value platform'>{t('admin.platformUsage.other', 'Other')}</span>
                    </div>
                    {renderDeliveryRateRow(
                        t('admin.platformUsage.deliveryRate1h', 'Bevestigingspercentage (afgelopen uur)'),
                        push_delivery_stats.delivery_1h
                    )}
                    {renderDeliveryRateRow(
                        t('admin.platformUsage.deliveryRate24h', 'Bevestigingspercentage (vandaag)'),
                        push_delivery_stats.delivery_24h
                    )}
                    {renderDeliveryRateRow(
                        t('admin.platformUsage.deliveryRate7d', 'Bevestigingspercentage (deze week)'),
                        push_delivery_stats.delivery_7d
                    )}
                    {renderDeliveryCountRow(
                        t('admin.platformUsage.deactivatedUnconfirmed7d', 'Gedeactiveerd (onbevestigd, deze week)'),
                        push_delivery_stats.delivery_7d,
                        'deactivated_unconfirmed'
                    )}
                </div>
            </div>
        );
    }

    function renderFilters() {
        return (
            <div className='analytics-filters'>
                <div className='filters-header'>
                    <span className='filters-title'>{t('admin.platformUsage.filters', 'Filters')}</span>
                    <BluemateButton
                        onClick={onResetFilters}
                        label={t('admin.platformUsage.resetFilters', 'Filters resetten')}
                        secondary
                        ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                    />
                </div>
                <div className='filter-row'>
                    <div className='filter-group'>
                        <label>{t('admin.platformUsage.from', 'Van')}</label>
                        <BluemateInput
                            value={filters.fromDate}
                            onChange={(value: string) => onUpdateFilter('fromDate', value)}
                            inputProps={{type: 'date', max: filters.toDate || today}}
                        />
                    </div>
                    <div className='filter-group'>
                        <label>{t('admin.platformUsage.to', 'Tot')}</label>
                        <BluemateInput
                            value={filters.toDate}
                            onChange={(value: string) => onUpdateFilter('toDate', value)}
                            inputProps={{type: 'date', min: filters.fromDate, max: today}}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.period', 'Periode')}</label>
                            {renderHelp(
                                <>
                                    <strong>{t('admin.platformUsage.bucket.auto', 'Auto')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.periodTooltipAuto',
                                        'Kiest automatisch de beste periode op basis van het datumbereik.'
                                    )}
                                    <br />
                                    <strong>{t('admin.platformUsage.bucket.day', 'Dag')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.periodTooltipDay',
                                        'Toont data per dag (alleen beschikbaar bij een bereik van maximaal 31 dagen).'
                                    )}
                                    <br />
                                    <strong>{t('admin.platformUsage.bucket.week', 'Week')}:</strong>{' '}
                                    {t('admin.platformUsage.periodTooltipWeek', 'Groepeert data per week.')}
                                    <br />
                                    <strong>{t('admin.platformUsage.bucket.month', 'Maand')}:</strong>{' '}
                                    {t('admin.platformUsage.periodTooltipMonth', 'Groepeert data per maand.')}
                                </>
                            )}
                        </div>
                        <BluemateSelect
                            value={filters.bucket}
                            onChange={(value: string) => onUpdateFilter('bucket', value as BucketOption)}
                            items={BUCKET_OPTIONS.map(opt => ({
                                value: opt.value,
                                label:
                                    opt.value === 'day' && !isDayBucketAllowed
                                        ? `${opt.label} ${t('admin.platformUsage.max31Days', '(max 31 dagen)')}`
                                        : opt.label,
                                disabled: opt.value === 'day' && !isDayBucketAllowed
                            }))}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.metricLabel', 'Metriek')}</label>
                            {renderHelp(
                                <>
                                    <strong>{t('admin.platformUsage.metric.uniqueUsers', 'Unieke gebruikers')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.metricTooltipUnique',
                                        'Telt hoeveel verschillende personen de pagina hebben bezocht.'
                                    )}
                                    <br />
                                    <strong>
                                        {t('admin.platformUsage.metric.totalEvents', 'Totaal events')}:
                                    </strong>{' '}
                                    {t('admin.platformUsage.metricTooltipTotal', 'Telt elk paginabezoek afzonderlijk.')}
                                </>
                            )}
                        </div>
                        <BluemateSelect
                            value={filters.metric}
                            onChange={(value: string) => onUpdateFilter('metric', value as MetricOption)}
                            items={METRIC_OPTIONS}
                        />
                    </div>
                </div>
                <div className='filter-row'>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.eventType', 'Event type')}</label>
                            {renderHelp(
                                <>
                                    <strong>{t('admin.platformUsage.event.pageView', 'Page views')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.eventTooltipPageView',
                                        'Paginabezoeken — wanneer een gebruiker een pagina opent.'
                                    )}
                                    <br />
                                    <strong>
                                        {t('admin.platformUsage.event.actionSuccess', 'Acties (succes)')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.eventTooltipSuccess',
                                        'Succesvolle acties zoals opslaan, exporteren, versturen.'
                                    )}
                                    <br />
                                    <strong>
                                        {t('admin.platformUsage.event.actionFailed', 'Acties (gefaald)')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.eventTooltipFailed',
                                        'Mislukte acties (bijv. validatiefouten).'
                                    )}
                                </>
                            )}
                        </div>
                        <BluemateSelect
                            value={filters.eventName}
                            onChange={(value: string) => onUpdateFilter('eventName', value as EventNameOption)}
                            items={EVENT_OPTIONS}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.groupBy', 'Groeperen op')}</label>
                            {renderHelp(
                                <>
                                    <strong>{t('admin.platformUsage.breakdown.featureGroup', 'Feature groep')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.groupByTooltipGroup',
                                        'Groepeert per categorie (bijv. Settings, Invoices, Emails).'
                                    )}
                                    <br />
                                    <strong>{t('admin.platformUsage.breakdown.featureKey', 'Feature')}:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.groupByTooltipFeature',
                                        'Toont individuele functies (bijv. settings.api, invoices.list).'
                                    )}
                                </>
                            )}
                        </div>
                        <BluemateSelect
                            value={filters.breakdown}
                            onChange={(value: string) => onUpdateFilter('breakdown', value as BreakdownOption)}
                            items={BREAKDOWN_OPTIONS}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.topNLabel', 'Top N')}</label>
                            {renderHelp(
                                <>
                                    {t(
                                        'admin.platformUsage.topNTooltipIntro',
                                        'Bepaalt hoeveel items worden weergegeven in de grafiek en tabel.'
                                    )}
                                    <br />
                                    <strong>{t('admin.platformUsage.topN.all', 'Alles')}:</strong>{' '}
                                    {t('admin.platformUsage.topNTooltipAll', 'Toont alle beschikbare items.')}
                                </>
                            )}
                        </div>
                        <BluemateSelect
                            value={String(filters.topN)}
                            onChange={(value: string) => onUpdateFilter('topN', parseInt(value))}
                            items={TOP_N_OPTIONS.map(opt => ({value: String(opt.value), label: opt.label}))}
                        />
                    </div>
                    <div className='filter-group user-search'>
                        <div className='label-with-help'>
                            <label>{t('admin.platformUsage.searchUser', 'Gebruiker zoeken')}</label>
                            {renderHelp(
                                <>
                                    {t(
                                        'admin.platformUsage.searchTooltip',
                                        'Zoekt in: e-mailadres, voornaam, achternaam, telefoonnummer en bedrijfsnaam van het account.'
                                    )}
                                </>
                            )}
                        </div>
                        <BluemateInput
                            value={filters.userSearch}
                            onChange={(value: string) => onUpdateFilter('userSearch', value)}
                            inputProps={{
                                placeholder: t('admin.platformUsage.searchPlaceholder', 'E-mail, naam, telefoon...')
                            }}
                        />
                    </div>
                </div>
                <div className='filter-row user-type-filters'>
                    <span className='filter-row-label'>{t('admin.platformUsage.userType', 'Gebruikertype')}:</span>
                    {renderHelp(
                        <>
                            <strong>{t('admin.platformUsage.userTypeTooltipNone', 'Geen selectie')}:</strong>{' '}
                            {t('admin.platformUsage.userTypeTooltipNoneDesc', 'Toont alle gebruikers.')}
                            <br />
                            <strong>{t('admin.platformUsage.regularUsers', 'Reguliere gebruikers')}:</strong>{' '}
                            {t('admin.platformUsage.userTypeTooltipRegularDesc', 'Normale gebruikers (niet-admins).')}
                            <br />
                            <strong>{t('admin.platformUsage.admins', 'Admins')}:</strong>{' '}
                            {t('admin.platformUsage.userTypeTooltipAdminDesc', 'Admin/support accounts.')}
                            <br />
                            <strong>{t('admin.platformUsage.adminImpersonation', 'Admin-impersonatie')}:</strong>{' '}
                            {t(
                                'admin.platformUsage.userTypeTooltipImpersonateDesc',
                                'Admin ingelogd als een andere gebruiker.'
                            )}
                        </>
                    )}
                    <Checkbox
                        checked={filters.includeRegularUsers}
                        onCheck={(checked: boolean) => onUpdateFilter('includeRegularUsers', checked)}
                        label={t('admin.platformUsage.regularUsers', 'Reguliere gebruikers')}
                    />
                    <Checkbox
                        checked={filters.includeAdmins}
                        onCheck={(checked: boolean) => onUpdateFilter('includeAdmins', checked)}
                        label={t('admin.platformUsage.admins', 'Admins')}
                    />
                    <Checkbox
                        checked={filters.includeAdminAsUser}
                        onCheck={(checked: boolean) => onUpdateFilter('includeAdminAsUser', checked)}
                        label={t('admin.platformUsage.adminImpersonation', 'Admin-impersonatie')}
                    />
                </div>
                {filters.featureKey && (
                    <div className='filter-row drill-down-info'>
                        <span>
                            {t('admin.platformUsage.drillDown', 'Drill-down')}: <strong>{filters.featureKey}</strong>
                        </span>
                        <BluemateButton
                            onClick={onClearDrillDown}
                            label={t('admin.platformUsage.clearFilter', 'Wis filter')}
                            secondary
                            ornaments={{element: <AwesomeIcon name='times' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Global timeseries chart
    const globalChartData = useMemo(() => {
        if (!data?.timeseries_global?.length) return null;

        const categories = data.timeseries_global.map(p => p.period);
        const values = data.timeseries_global.map(p => p.value);
        const yAxisConfig = buildYAxisConfig(values);

        const options = buildBaseChartOptions(
            categories,
            yAxisConfig,
            t('admin.platformUsage.chart.platformActivity', 'Platform activiteit over tijd'),
            'line',
            {
                colors: ['#008FFB']
            },
            isMobile
        );

        const series = [
            {
                name:
                    filters.metric === 'unique_users'
                        ? t('admin.platformUsage.metric.uniqueUsers', 'Unieke gebruikers')
                        : t('admin.platformUsage.events', 'Events'),
                data: values
            }
        ];

        return {options, series};
    }, [data?.timeseries_global, filters.metric, isMobile]);

    // Top categories line chart
    const topCategoriesChartData = useMemo(() => {
        if (!data?.timeseries_top?.length) return null;

        // Get all unique periods
        const allPeriods = new Set<string>();
        data.timeseries_top.forEach(series => {
            series.points.forEach(p => allPeriods.add(p.period));
        });
        const categories = Array.from(allPeriods).sort();

        // Build series data
        const series = data.timeseries_top.map(s => ({
            name: s.seriesKey,
            data: categories.map(period => {
                const point = s.points.find(p => p.period === period);
                return point?.value ?? 0;
            })
        }));

        const allValues = series.flatMap(s => s.data);
        const yAxisConfig = buildYAxisConfig(allValues);

        const groupsOrFeatures =
            filters.breakdown === 'feature_group'
                ? t('admin.platformUsage.groups', 'groepen')
                : t('admin.platformUsage.features', 'features');
        const title =
            filters.topN === 0
                ? `${t('admin.platformUsage.allOf', 'Alle')} ${groupsOrFeatures}`
                : `${t('admin.platformUsage.topWord', 'Top')} ${filters.topN} ${groupsOrFeatures}`;

        const options = buildBaseChartOptions(
            categories,
            yAxisConfig,
            title,
            'line',
            {
                chart: {type: 'line', stacked: false, toolbar: {show: false}, zoom: {enabled: false}},
                markers: {size: isMobile ? 3 : 6, strokeWidth: 2, hover: {size: 8}},
                legend: {position: 'top', horizontalAlign: 'left'},
                tooltip: {shared: true, intersect: false}
            },
            isMobile
        );

        return {options, series};
    }, [data?.timeseries_top, filters.topN, filters.breakdown, isMobile]);

    // Drill-down chart
    const drilldownChartData = useMemo(() => {
        if (!data?.feature_drilldown) return null;
        const {page_views, actions} = data.feature_drilldown;

        if (!page_views?.length) return null;

        const categories = page_views.map(p => p.period);
        const pageViewValues = page_views.map(p => p.value);
        const actionValues = actions.map(p => p.value);

        const yAxisConfig = buildYAxisConfig([...pageViewValues, ...actionValues]);

        const options = buildBaseChartOptions(
            categories,
            yAxisConfig,
            `${t('admin.platformUsage.detail', 'Detail')}: ${filters.featureKey}`,
            'line',
            {
                yaxis: [
                    {
                        ...yAxisConfig,
                        reversed: false,
                        title: {text: t('admin.platformUsage.events', 'Events')},
                        labels: {formatter: (val: number) => Math.round(val).toString()}
                    }
                ],
                legend: {position: 'top'},
                colors: ['#008FFB', '#00E396']
            },
            isMobile
        );

        const series = [
            {name: t('admin.platformUsage.pageViews', 'Page views'), data: pageViewValues},
            {name: t('admin.platformUsage.actions', 'Acties'), data: actionValues}
        ];

        return {options, series};
    }, [data?.feature_drilldown, filters.featureKey, isMobile]);

    // User growth chart (total registered users over time)
    const userGrowthChartData = useMemo(() => {
        if (!data?.timeseries_user_growth?.length) return null;

        const categories = data.timeseries_user_growth.map(p => p.period);
        const values = data.timeseries_user_growth.map(p => p.value);

        const yAxisConfig = buildYAxisConfig(values, {padding: 0.05, startFromZero: false});

        const options = buildBaseChartOptions(
            categories,
            yAxisConfig,
            t('admin.platformUsage.chart.totalUsers', 'Totaal gebruikers op platform'),
            'area',
            {
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.4,
                        opacityTo: 0.1,
                        stops: [0, 100]
                    }
                },
                colors: ['#00E396']
            },
            isMobile
        );

        const series = [
            {
                name: t('admin.platformUsage.totalUsersSeriesName', 'Totaal gebruikers'),
                data: values
            }
        ];

        return {options, series};
    }, [data?.timeseries_user_growth, isMobile]);

    function renderHelp(content: ReactNode) {
        return (
            <BluemateTooltip className='platform-usage-help' text={content} interactive>
                <span className='help-icon'>
                    <InfoIcon />
                </span>
            </BluemateTooltip>
        );
    }

    function renderDetailButton(item: TopTableItem) {
        return (
            <BluemateButton
                onClick={() => onDrillDown(item.key)}
                label={t('admin.platformUsage.detail', 'Detail')}
                secondary
                ornaments={{element: <AwesomeIcon name='search' />, placement: ElementPosition.LEFT}}
            />
        );
    }

    function renderTopCards(items: TopTableItem[]) {
        return (
            <ul className='top-card-list'>
                {items.map(item => (
                    <li className='top-card' key={item.key}>
                        <div className='top-card-head'>
                            <span className='top-card-title'>{item.title}</span>
                            {!!item.group && <span className='top-card-group'>{item.group}</span>}
                        </div>
                        <div className='top-card-metrics'>
                            {TOP_TABLE_METRICS.map(metric => (
                                <div className='top-card-metric' key={metric.key}>
                                    <span className='metric-label'>{metric.label}</span>
                                    <span className='metric-value'>{metric.value(item)}</span>
                                </div>
                            ))}
                        </div>
                        <div className='top-card-actions'>{renderDetailButton(item)}</div>
                    </li>
                ))}
            </ul>
        );
    }

    function renderTopTable() {
        if (!data?.top_table?.length) return null;
        const items = data.top_table;

        return (
            <div className='top-table-section'>
                <div className='top-table-header'>
                    <h3>
                        {filters.topN === 0
                            ? t('admin.platformUsage.allOf', 'Alle')
                            : `${t('admin.platformUsage.topWord', 'Top')} ${filters.topN}`}{' '}
                        {filters.breakdown === 'feature_group'
                            ? t('admin.platformUsage.groups', 'groepen')
                            : t('admin.platformUsage.features', 'features')}
                    </h3>
                    {renderHelp(
                        t(
                            'admin.platformUsage.topTableTooltip',
                            'Deze tabel toont de meest gebruikte features of groepen op basis van de geselecteerde filters. Klik op "Detail" om dieper in te zoomen op een specifieke feature. Na het klikken op Detail kunt u nogmaals een subgroep selecteren om de weergave verder te verfijnen en een gedetailleerde grafiek te bekijken.'
                        )
                    )}
                </div>
                {compactLayout ? (
                    renderTopCards(items)
                ) : (
                    <table className='top-table'>
                        <thead>
                            <tr>
                                <th>
                                    <span>{t('admin.platformUsage.tbl.name', 'Naam')}</span>
                                    {renderHelp('De naam van de feature of groep.')}
                                </th>
                                <th>
                                    <span>{t('admin.platformUsage.tbl.group', 'Groep')}</span>
                                    {renderHelp(
                                        'De categorie waartoe deze feature behoort (bijv. Settings, Emails, Invoices).'
                                    )}
                                </th>
                                {TOP_TABLE_METRICS.map(metric => (
                                    <th key={metric.key}>
                                        <span>{metric.label}</span>
                                        {renderHelp(metric.help)}
                                    </th>
                                ))}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.key}>
                                    <td>{item.title}</td>
                                    <td>{item.group}</td>
                                    {TOP_TABLE_METRICS.map(metric => (
                                        <td key={metric.key}>{metric.value(item)}</td>
                                    ))}
                                    <td>{renderDetailButton(item)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }

    function formatTimeRemaining(seconds: number): string {
        if (seconds <= 0) return '0 min';
        const minutes = Math.ceil(seconds / 60);
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}u ${remainingMinutes}min` : `${hours}u`;
    }

    function formatCooldownTime(seconds: number): string {
        if (seconds <= 0) return '0s';
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.ceil(seconds / 60);
        return `${minutes} min`;
    }

    function renderAnalyticsSection() {
        const aggregationDelay = data?.aggregation_delay_minutes ?? 60;
        const cooldown = data?.aggregation_cooldown;
        // Use interactive countdown instead of server value
        const canRunAggregation = cooldownRemainingSeconds <= 0;
        const cooldownTotalMinutes = cooldown ? Math.ceil(cooldown.cooldown_total_seconds / 60) : 15;

        // Pending events info
        const readyEventsCount = cooldown?.ready_events_count ?? 0;
        const pendingEventsCount = cooldown?.pending_events_count ?? 0;
        const pendingReadyInSeconds = cooldown?.pending_ready_in_seconds ?? 0;

        return (
            <div className='analytics-section'>
                <div className='analytics-header'>
                    <div className='analytics-header-row'>
                        <h2>{t('admin.platformUsage.usageAnalytics', 'Usage Analytics')}</h2>
                        <div className='aggregation-button-container'>
                            <BluemateButton
                                onClick={onRunAggregation}
                                label={
                                    !canRunAggregation
                                        ? `${t('admin.platformUsage.wait', 'Wacht')} ${formatCooldownTime(cooldownRemainingSeconds)}`
                                        : readyEventsCount > 0
                                          ? `${t('admin.platformUsage.aggregate', 'Aggregeer')} ${readyEventsCount} ${t('admin.platformUsage.eventsWord', 'events')}`
                                          : t('admin.platformUsage.runAggregation', 'Aggregatie uitvoeren')
                                }
                                secondary
                                disabled={!canRunAggregation}
                                ornaments={{element: <AwesomeIcon name='cogs' />, placement: ElementPosition.LEFT}}
                            />
                            {renderHelp(
                                <>
                                    <strong>
                                        {t('admin.platformUsage.aggregationTooltipTitle', 'Handmatige aggregatie')}
                                    </strong>
                                    <br />
                                    <br />
                                    {t(
                                        'admin.platformUsage.aggregationTooltipDesc',
                                        'Deze knop voert direct de data-aggregatie uit, zonder te wachten op de automatische verwerking (elke {{delay}} minuten).',
                                        {delay: aggregationDelay}
                                    )}
                                    <br />
                                    <br />
                                    <strong>
                                        {t('admin.platformUsage.aggregationTooltipDelayLabel', 'Vertraging')}:
                                    </strong>{' '}
                                    {t(
                                        'admin.platformUsage.aggregationTooltipDelayDesc',
                                        'Events moeten minimaal {{delay}} minuten oud zijn voordat ze kunnen worden geaggregeerd (om race conditions te voorkomen).',
                                        {delay: aggregationDelay}
                                    )}
                                    <br />
                                    <br />
                                    <strong>Cooldown:</strong>{' '}
                                    {t(
                                        'admin.platformUsage.aggregationTooltipCooldownDesc',
                                        'Na het uitvoeren moet u {{minutes}} minuten wachten voordat u opnieuw kunt aggregeren. Dit geldt voor alle beheerders.',
                                        {minutes: cooldownTotalMinutes}
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div className='aggregation-info'>
                        <span className='info-section'>
                            {t('admin.platformUsage.aggregatedWith', 'Data wordt geaggregeerd met')} {aggregationDelay}{' '}
                            {t('admin.platformUsage.minutesDelay', 'minuten vertraging')}
                        </span>
                        {readyEventsCount > 0 && (
                            <span className='info-section'>
                                <strong>
                                    {readyEventsCount} {t('admin.platformUsage.eventsWord', 'events')}
                                </strong>{' '}
                                {t('admin.platformUsage.readyForAggregation', 'klaar voor aggregatie')}
                            </span>
                        )}
                        {pendingEventsCount > 0 && readyEventsCount === 0 && (
                            <span className='info-section'>
                                <strong>
                                    {pendingEventsCount} {t('admin.platformUsage.eventsWord', 'events')}
                                </strong>{' '}
                                {t('admin.platformUsage.pendingReadyIn', 'wachtend (gereed over')}{' '}
                                {formatTimeRemaining(pendingReadyInSeconds)})
                            </span>
                        )}
                        {pendingEventsCount > 0 && readyEventsCount > 0 && (
                            <span className='info-section'>
                                <strong>
                                    {pendingEventsCount} {t('admin.platformUsage.eventsWord', 'events')}
                                </strong>{' '}
                                {t('admin.platformUsage.pending', 'wachtend')}
                            </span>
                        )}
                    </div>
                </div>
                {renderFilters()}

                {!isDateRangeValid ? (
                    // Case 0: Invalid date range (fromDate > toDate)
                    <div className='no-data-message warning'>
                        <p>
                            <strong>{t('admin.platformUsage.invalidRangeTitle', 'Ongeldige datumbereik')}</strong>
                        </p>
                        <p>
                            {t('admin.platformUsage.invalidRangeMsg1a', 'De "Van" datum')} ({filters.fromDate}){' '}
                            {t('admin.platformUsage.invalidRangeMsg1b', 'is later dan de "Tot" datum')} (
                            {filters.toDate}).
                        </p>
                        <p>
                            {t(
                                'admin.platformUsage.invalidRangeMsg2',
                                'Pas de datums aan zodat "Van" eerder is dan of gelijk aan "Tot".'
                            )}
                        </p>
                        <BluemateButton
                            onClick={onResetFilters}
                            label={t('admin.platformUsage.resetFilters', 'Filters resetten')}
                            secondary
                            ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                ) : !hasAnyData ? (
                    // Case 1: No data exists in the system at all
                    <div className='no-data-message'>
                        <p>{t('admin.platformUsage.noAnalyticsData', 'Geen analytics data beschikbaar.')}</p>
                        <p>
                            {t(
                                'admin.platformUsage.eventsAutoCollected',
                                'Analytics events worden automatisch verzameld zodra gebruikers de platform gebruiken.'
                            )}
                        </p>
                        <p>
                            {t(
                                'admin.platformUsage.clickToAggregate',
                                'Klik op de knop hieronder om verzamelde events te aggregeren.'
                            )}
                        </p>
                        <BluemateButton
                            onClick={onRunAggregation}
                            label={
                                !canRunAggregation
                                    ? `${t('admin.platformUsage.wait', 'Wacht')} ${formatCooldownTime(cooldownRemainingSeconds)}`
                                    : readyEventsCount > 0
                                      ? `${t('admin.platformUsage.aggregate', 'Aggregeer')} ${readyEventsCount} ${t('admin.platformUsage.eventsWord', 'events')}`
                                      : t('admin.platformUsage.runAggregation', 'Aggregatie uitvoeren')
                            }
                            secondary
                            disabled={!canRunAggregation}
                            ornaments={{element: <AwesomeIcon name='play' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                ) : (
                    // Case 2 & 3: Data exists (may or may not have filtered results)
                    <>
                        {!hasFilteredResults ? (
                            // Case 2: Data exists but current filters return no results for Feature activiteit
                            <div className='no-data-message'>
                                <p>
                                    {t(
                                        'admin.platformUsage.noFilterResults',
                                        'De huidige filterinstellingen geven geen resultaten voor Feature activiteit.'
                                    )}
                                </p>
                                <p>
                                    {t(
                                        'admin.platformUsage.noFilterResultsExplanation',
                                        'Mogelijk is de verzamelde data voor andere gebruikertypes dan geselecteerd in de sectie "Gebruikertype", of er zijn geen events in de geselecteerde periode.'
                                    )}
                                </p>
                                <p>
                                    {t(
                                        'admin.platformUsage.tryOtherPeriod',
                                        'Probeer een andere periode, andere gebruikertypes te selecteren of reset de filters.'
                                    )}
                                </p>
                                <BluemateButton
                                    onClick={onResetFilters}
                                    label={t('admin.platformUsage.resetFilters', 'Filters resetten')}
                                    secondary
                                    ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                                />
                            </div>
                        ) : (
                            // Case 3: Filters return results - show Feature activiteit charts
                            <>
                                <div className='charts-section'>
                                    <div className='charts-section-header'>
                                        <h3>{t('admin.platformUsage.featureActivity', 'Feature activiteit')}</h3>
                                        {renderHelp(
                                            <>
                                                {t(
                                                    'admin.platformUsage.chartTooltipIntro',
                                                    'Deze grafieken tonen het gebruik van features op basis van de geselecteerde filters.'
                                                )}
                                                <br />
                                                <br />
                                                <strong>
                                                    {t(
                                                        'admin.platformUsage.chartTooltipActivityLabel',
                                                        'Platform activiteit'
                                                    )}
                                                    :
                                                </strong>{' '}
                                                {filters.metric === 'unique_users'
                                                    ? t(
                                                          'admin.platformUsage.chartTooltipActivityUnique',
                                                          'Toont unieke gebruikers die features hebben gebruikt in de geselecteerde periode.'
                                                      )
                                                    : t(
                                                          'admin.platformUsage.chartTooltipActivityTotal',
                                                          'Toont totaal aantal events in de geselecteerde periode.'
                                                      )}
                                                <br />
                                                <br />
                                                <strong>
                                                    {t(
                                                        'admin.platformUsage.chartTooltipTopLabel',
                                                        'Top groepen/features'
                                                    )}
                                                    :
                                                </strong>{' '}
                                                {filters.breakdown === 'feature_group'
                                                    ? t(
                                                          'admin.platformUsage.chartTooltipTopGroups',
                                                          'Toont de meest populaire feature groepen.'
                                                      )
                                                    : t(
                                                          'admin.platformUsage.chartTooltipTopFeatures',
                                                          'Toont de meest populaire features.'
                                                      )}
                                            </>
                                        )}
                                    </div>
                                    <div className='charts-grid'>
                                        <div className='chart-container'>
                                            {globalChartData ? (
                                                <Chart
                                                    options={globalChartData.options}
                                                    series={globalChartData.series}
                                                    type='line'
                                                    height={chartHeight}
                                                />
                                            ) : loading ? (
                                                <div className='chart-loading'>
                                                    <Loader active />
                                                </div>
                                            ) : (
                                                <div className='chart-no-data'>
                                                    <p>
                                                        {t(
                                                            'admin.platformUsage.noDataPeriod',
                                                            'Geen data beschikbaar voor de geselecteerde periode.'
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className='chart-container'>
                                            {topCategoriesChartData ? (
                                                <Chart
                                                    options={topCategoriesChartData.options}
                                                    series={topCategoriesChartData.series}
                                                    type='line'
                                                    height={chartHeight}
                                                />
                                            ) : loading ? (
                                                <div className='chart-loading'>
                                                    <Loader active />
                                                </div>
                                            ) : (
                                                <div className='chart-no-data'>
                                                    <p>
                                                        {t(
                                                            'admin.platformUsage.noDataPeriod',
                                                            'Geen data beschikbaar voor de geselecteerde periode.'
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {(drilldownChartData || (loading && filters.featureKey)) && (
                                            <div className='chart-container full-width'>
                                                {drilldownChartData ? (
                                                    <Chart
                                                        options={drilldownChartData.options}
                                                        series={drilldownChartData.series}
                                                        type='line'
                                                        height={chartHeight}
                                                    />
                                                ) : loading ? (
                                                    <div className='chart-loading'>
                                                        <Loader active />
                                                    </div>
                                                ) : (
                                                    <div className='chart-no-data'>
                                                        <p>
                                                            {t(
                                                                'admin.platformUsage.noDrillDownData',
                                                                'Geen drill-down data beschikbaar.'
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {renderTopTable()}
                            </>
                        )}

                        {/* Gebruikersgroei section - always visible when hasAnyData, independent of hasFilteredResults */}
                        <div className='charts-section user-growth-section'>
                            <div className='charts-section-header'>
                                <h3>{t('admin.platformUsage.userGrowth', 'Gebruikersgroei')}</h3>
                                {renderHelp(
                                    <>
                                        {t(
                                            'admin.platformUsage.growthTooltip1',
                                            'Dit is het cumulatieve totaal van geregistreerde gebruikers op het platform.'
                                        )}
                                        <br />
                                        <br />
                                        {t(
                                            'admin.platformUsage.growthTooltip2',
                                            'Deze grafiek toont het totale aantal accounts, niet hoeveel gebruikers een specifieke feature hebben gebruikt (zoals in "Feature activiteit" hierboven).'
                                        )}
                                        <br />
                                        <br />
                                        {t(
                                            'admin.platformUsage.growthTooltip3',
                                            'Alleen de filters Van-Tot en Periode zijn van toepassing op deze grafiek.'
                                        )}
                                    </>
                                )}
                            </div>
                            <div className='charts-grid'>
                                <div className='chart-container full-width'>
                                    {userGrowthChartData ? (
                                        <Chart
                                            options={userGrowthChartData.options}
                                            series={userGrowthChartData.series}
                                            type='area'
                                            height={chartHeight}
                                        />
                                    ) : loading ? (
                                        <div className='chart-loading'>
                                            <Loader active />
                                        </div>
                                    ) : (
                                        <div className='chart-no-data'>
                                            <p>
                                                {t(
                                                    'admin.platformUsage.noGrowthData',
                                                    'Geen gebruikersgroei data beschikbaar voor de geselecteerde periode.'
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <Loader active={loading} />
            </div>
        );
    }

    function renderAnalyticsSettings() {
        if (!analyticsSettings || !editableSettings) return null;

        const {data_counts, limits} = analyticsSettings;
        const totalRecords =
            data_counts.raw_events +
            data_counts.user_day_records +
            data_counts.daily_aggregates +
            (data_counts.push_counters || 0);
        const isDisabled = settingsLoading || settingsSaving;

        return (
            <div className='analytics-settings-section'>
                <div className='settings-header'>
                    <h3>{t('admin.platformUsage.analyticsSettings', 'Analytics Instellingen')}</h3>
                    {renderHelp(
                        <>
                            {t(
                                'admin.platformUsage.settingsTooltipIntro',
                                'Beheer hier de analytics dataverzameling en -opslag.'
                            )}
                            <br />
                            <br />
                            <strong>{t('admin.platformUsage.dataCollection', 'Dataverzameling')}:</strong>{' '}
                            {t(
                                'admin.platformUsage.settingsTooltipCollection',
                                'Als uitgeschakeld, worden er geen nieuwe analytics events opgeslagen.'
                            )}
                            <br />
                            <br />
                            <strong>{t('admin.platformUsage.retention', 'Data Retentie & Aggregation')}:</strong>{' '}
                            {t(
                                'admin.platformUsage.settingsTooltipRetention',
                                'Na de opgegeven periode worden oude records automatisch verwijderd.'
                            )}
                            <br />
                            <br />
                            <strong>{t('admin.platformUsage.aggregationDelayLabel', 'Aggregation delay')}:</strong>{' '}
                            {t(
                                'admin.platformUsage.settingsTooltipDelay',
                                'Tijd voordat events worden geaggregeerd (voorkomt race conditions).'
                            )}
                        </>
                    )}
                </div>
                <div className='settings-content'>
                    <div className='setting-row'>
                        <div className='setting-label'>
                            <span>{t('admin.platformUsage.dataCollection', 'Dataverzameling')}</span>
                            <span className='setting-description'>
                                {editableSettings.collection_enabled
                                    ? t('admin.platformUsage.eventsStored', 'Analytics events worden opgeslagen')
                                    : t(
                                          'admin.platformUsage.eventsNotStored',
                                          'Analytics events worden NIET opgeslagen'
                                      )}
                            </span>
                        </div>
                        <BluemateSwitch
                            value={editableSettings.collection_enabled}
                            onChange={(checked: boolean) => onUpdateEditableSetting('collection_enabled', checked)}
                            disabled={isDisabled}
                            activeGreen
                        />
                    </div>
                    <div className='retention-settings'>
                        <h4>{t('admin.platformUsage.retention', 'Data Retentie & Aggregation')}</h4>
                        <div className='settings-grid'>
                            <div className='setting-input-row'>
                                <label>
                                    {t('admin.platformUsage.rawRetention', 'Ruwe events retentie (max')}{' '}
                                    {limits.max_raw_events_days} {t('admin.platformUsage.days', 'dagen')})
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.raw_events_retention_days}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(
                                                limits.max_raw_events_days,
                                                Math.max(1, value.numberValue || 1)
                                            );
                                            onUpdateEditableSetting('raw_events_retention_days', v);
                                        }}
                                        inputProps={{min: 1, max: limits.max_raw_events_days}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.days', 'dagen')}</span>
                                </div>
                                <span className='record-count'>
                                    {data_counts.raw_events.toLocaleString()}{' '}
                                    {t('admin.platformUsage.records', 'records')}
                                </span>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t('admin.platformUsage.userDayRetention', 'Gebruiker-dag retentie (max')}{' '}
                                    {limits.max_user_day_days} {t('admin.platformUsage.days', 'dagen')})
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.user_day_retention_days}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(
                                                limits.max_user_day_days,
                                                Math.max(1, value.numberValue || 1)
                                            );
                                            onUpdateEditableSetting('user_day_retention_days', v);
                                        }}
                                        inputProps={{min: 1, max: limits.max_user_day_days}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.days', 'dagen')}</span>
                                </div>
                                <span className='record-count'>
                                    {data_counts.user_day_records.toLocaleString()}{' '}
                                    {t('admin.platformUsage.records', 'records')}
                                </span>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t(
                                        'admin.platformUsage.dailyAggregatesRetention',
                                        'Dagelijkse aggregaten retentie (0 = onbeperkt)'
                                    )}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.daily_aggregates_retention_days}
                                        onChange={(value: NumberString) => {
                                            const v = Math.max(0, value.numberValue || 0);
                                            onUpdateEditableSetting('daily_aggregates_retention_days', v);
                                        }}
                                        inputProps={{min: 0}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.days', 'dagen')}</span>
                                </div>
                                <span className='record-count'>
                                    {data_counts.daily_aggregates.toLocaleString()}{' '}
                                    {t('admin.platformUsage.records', 'records')}
                                </span>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t('admin.platformUsage.aggregationDelay', 'Aggregation delay (min 1 minuut)')}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.aggregation_delay_minutes}
                                        onChange={(value: NumberString) => {
                                            const v = Math.max(1, value.numberValue || 1);
                                            onUpdateEditableSetting('aggregation_delay_minutes', v);
                                        }}
                                        inputProps={{min: 1}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.minutes', 'minuten')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='retention-settings'>
                        <h4>{t('admin.platformUsage.pushDeliverySettings', 'Web Push Opschonen')}</h4>
                        <div className='settings-grid'>
                            <div className='setting-input-row'>
                                <label>
                                    {t(
                                        'admin.platformUsage.pushCounterRetention',
                                        'Delivery statistieken retentie (max 365 dagen)'
                                    )}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.push_counter_retention_days}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(365, Math.max(1, value.numberValue || 1));
                                            onUpdateEditableSetting('push_counter_retention_days', v);
                                        }}
                                        inputProps={{min: 1, max: 365}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.days', 'dagen')}</span>
                                </div>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t(
                                        'admin.platformUsage.pushStaleTokenDays',
                                        'Inactieve apparaten verwijderen na (max 365 dagen)'
                                    )}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.push_stale_token_days}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(365, Math.max(1, value.numberValue || 1));
                                            onUpdateEditableSetting('push_stale_token_days', v);
                                        }}
                                        inputProps={{min: 1, max: 365}}
                                        disabled={isDisabled || !editableSettings.web_push_cleanup_enabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.days', 'dagen')}</span>
                                </div>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t(
                                        'admin.platformUsage.pushUnconfirmedThreshold',
                                        'Apparaat verwijderen na onbevestigde pushes'
                                    )}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.push_unconfirmed_threshold}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(50, Math.max(1, value.numberValue || 1));
                                            onUpdateEditableSetting('push_unconfirmed_threshold', v);
                                        }}
                                        inputProps={{min: 1, max: 50}}
                                        disabled={isDisabled || !editableSettings.web_push_unconfirmed_cleanup_enabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.pushes', 'pushes')}</span>
                                </div>
                            </div>
                            <div className='setting-input-row'>
                                <label>
                                    {t(
                                        'admin.platformUsage.pushTrayLimit',
                                        'Maximaal zichtbare meldingen per apparaat (1-20)'
                                    )}
                                </label>
                                <div className='input-with-suffix'>
                                    <BluemateNumberInput
                                        value={editableSettings.push_tray_limit}
                                        onChange={(value: NumberString) => {
                                            const v = Math.min(20, Math.max(1, value.numberValue || 1));
                                            onUpdateEditableSetting('push_tray_limit', v);
                                        }}
                                        inputProps={{min: 1, max: 20}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>{t('admin.platformUsage.notifications', 'meldingen')}</span>
                                </div>
                            </div>
                        </div>
                        <div className='setting-row'>
                            <div className='setting-label'>
                                <span>
                                    {t(
                                        'admin.platformUsage.webPushCleanupEnabled',
                                        'Inactieve apparaten automatisch verwijderen'
                                    )}
                                </span>
                                <span className='setting-description'>
                                    {editableSettings.web_push_cleanup_enabled
                                        ? t(
                                              'admin.platformUsage.webPushCleanupEnabledDesc',
                                              "Apparaten waarvan 'laatst gezien' ouder is dan het ingestelde aantal dagen worden automatisch verwijderd"
                                          )
                                        : t(
                                              'admin.platformUsage.webPushCleanupDisabledDesc',
                                              'Apparaten worden NOOIT automatisch verwijderd op basis van inactiviteit'
                                          )}
                                </span>
                            </div>
                            <BluemateSwitch
                                value={editableSettings.web_push_cleanup_enabled}
                                onChange={(checked: boolean) =>
                                    onUpdateEditableSetting('web_push_cleanup_enabled', checked)
                                }
                                disabled={isDisabled}
                                activeGreen
                            />
                        </div>
                        <div className='setting-row'>
                            <div className='setting-label'>
                                <span>
                                    {t(
                                        'admin.platformUsage.webPushUnconfirmedCleanupEnabled',
                                        'Apparaat verwijderen na onbevestigde pushes'
                                    )}
                                </span>
                                <span className='setting-description'>
                                    {editableSettings.web_push_unconfirmed_cleanup_enabled
                                        ? t(
                                              'admin.platformUsage.webPushUnconfirmedCleanupEnabledDesc',
                                              'Een apparaat wordt verwijderd zodra het ingestelde aantal pushes onbevestigd blijft'
                                          )
                                        : t(
                                              'admin.platformUsage.webPushUnconfirmedCleanupDisabledDesc',
                                              'Apparaten worden nooit verwijderd om onbevestigde pushes'
                                          )}
                                </span>
                            </div>
                            <BluemateSwitch
                                value={editableSettings.web_push_unconfirmed_cleanup_enabled}
                                onChange={(checked: boolean) =>
                                    onUpdateEditableSetting('web_push_unconfirmed_cleanup_enabled', checked)
                                }
                                disabled={isDisabled}
                                activeGreen
                            />
                        </div>
                    </div>
                    <div className='settings-actions'>
                        <BluemateButton
                            onClick={onSaveSettings}
                            label={t('admin.platformUsage.saveSettings', 'Instellingen opslaan')}
                            disabled={isDisabled || !settingsHasChanges}
                            ornaments={{element: <AwesomeIcon name='save' />, placement: ElementPosition.LEFT}}
                        />
                        <div className='spacer' />
                        {totalRecords === 0 && (
                            <span className='no-data-hint'>
                                {t('admin.platformUsage.noDataToClear', 'Er is geen data om te wissen')}
                            </span>
                        )}
                        <BluemateButton
                            onClick={onOpenClearDataModal}
                            label={t('admin.platformUsage.clearAllData', 'Alle data wissen')}
                            secondary
                            color={'red'}
                            disabled={isDisabled || totalRecords === 0}
                            ornaments={{element: <AwesomeIcon name='trash' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                </div>
                <Loader active={settingsSaving} />
            </div>
        );
    }

    function renderClearDataModal() {
        if (!analyticsSettings) return null;

        const {data_counts} = analyticsSettings;
        const pushCounters = data_counts.push_counters || 0;
        const totalRecords =
            data_counts.raw_events + data_counts.user_day_records + data_counts.daily_aggregates + pushCounters;

        return (
            <WithBackground
                active={clearDataModalActive}
                onClose={onCloseClearDataModal}
                preventBackgroundClick={clearDataLoading}
                escapeDisabled={clearDataLoading}
            >
                <div className='default-popup clear-analytics-data'>
                    <div className='header-container'>
                        <label className='header-title'>
                            {t('admin.platformUsage.clearModalTitle', 'Analytics data wissen')}
                        </label>
                        <CloseButton onClick={onCloseClearDataModal} disabled={clearDataLoading} />
                    </div>
                    <div className='body-container'>
                        <Loader active={clearDataLoading} />
                        <p className='body-message'>
                            <strong>{t('admin.platformUsage.warning', 'Let op')}:</strong>{' '}
                            {t('admin.platformUsage.clearWarn1', 'Deze actie verwijdert')}{' '}
                            <b>
                                {t('admin.platformUsage.all', 'alle')} ({totalRecords.toLocaleString()})
                            </b>{' '}
                            {t('admin.platformUsage.clearWarn2', 'analytics records permanent. Dit omvat:')}
                        </p>
                        <ul className='data-list'>
                            <li>
                                {data_counts.raw_events.toLocaleString()}{' '}
                                {t('admin.platformUsage.rawEvents', 'ruwe events')}
                            </li>
                            <li>
                                {data_counts.user_day_records.toLocaleString()}{' '}
                                {t('admin.platformUsage.userDayRecords', 'gebruiker-dag records')}
                            </li>
                            <li>
                                {data_counts.daily_aggregates.toLocaleString()}{' '}
                                {t('admin.platformUsage.dailyAggregates', 'dagelijkse aggregaten')}
                            </li>
                            {pushCounters > 0 && (
                                <li>
                                    {pushCounters.toLocaleString()}{' '}
                                    {t('admin.platformUsage.pushCounters', 'push delivery statistieken')}
                                </li>
                            )}
                        </ul>
                        <p className='body-message warning'>
                            <AwesomeIcon name='exclamation-triangle' />{' '}
                            {t(
                                'admin.platformUsage.clearWarnIrreversible',
                                'Deze actie kan niet ongedaan worden gemaakt. Alle historische analytics data gaat verloren.'
                            )}
                        </p>
                        <Checkbox
                            checked={clearDataConfirmed}
                            onCheck={(checked: boolean) => onSetClearDataConfirmed(checked)}
                            label={t(
                                'admin.platformUsage.confirmLabel',
                                'Ik bevestig dat alle analytics data permanent verwijderd moet worden en begrijp dat dit niet teruggedraaid kan worden'
                            )}
                        />
                    </div>
                    <div className='footer-container btn-2'>
                        <BluemateButton
                            onClick={onCloseClearDataModal}
                            label={t('admin.platformUsage.cancel', 'Annuleren')}
                            disabled={clearDataLoading}
                        />
                        <BluemateButton
                            onClick={onClearAllData}
                            label={t('admin.platformUsage.clearData', 'Data wissen')}
                            disabled={clearDataLoading || !clearDataConfirmed}
                            secondary
                        />
                    </div>
                </div>
            </WithBackground>
        );
    }

    return (
        <div className='platform-usage'>
            <div className='header'>
                <BluemateButton
                    onClick={onClickBack}
                    label={t('admin.platformUsage.back', 'Terug')}
                    disabled={loading}
                    secondary
                    ornaments={{element: <AwesomeIcon name='arrow-left' />, placement: ElementPosition.LEFT}}
                />
                <h2>{t('admin.platformUsage.pageTitle', 'Platformgebruik')}</h2>
                <BluemateButton
                    onClick={onRefresh}
                    label={t('admin.platformUsage.refresh', 'Vernieuwen')}
                    disabled={loading}
                    secondary
                    ornaments={{element: <AwesomeIcon name='sync' />, placement: ElementPosition.LEFT}}
                />
            </div>
            <div className='content'>
                {loading && !data && <Loader active />}
                {data && (
                    <>
                        <div className='snapshot-stats'>
                            {renderEmailStats()}
                            {renderNotificationStats()}
                            {renderAdditionalUsersStats()}
                            {renderPushDeliveryStats()}
                            {renderPwaInstallStats()}
                        </div>
                        {renderAnalyticsSection()}
                        {renderAnalyticsSettings()}
                    </>
                )}
            </div>
            {renderClearDataModal()}
        </div>
    );
}
